import { isStrayCypherExpr } from './stray'

/**
 * Detail bundle for a single `:Procedure` (`$procedureId` param).
 *
 * Returns one row:
 *   - `p`         — the Procedure node
 *   - `appliesTo` — `PROCEDURE_FOR` peers with `labels(peer)` (Equipment /
 *                   Line / Process / LineOperation / HumanLabor)
 *   - `parts`     — `REQUIRES_PART` rows `{ part, edge }`
 *   - `skills`    — `REQUIRES_SKILL_LEVEL` rows `{ role, props }`
 *   - `products`  — `APPLIES_TO_PRODUCT` Product chips
 *   - `site` / `line` / `breadcrumbSubject` / `breadcrumbSubjectLabels` —
 *                   Factory crumb context from the preferred PROCEDURE_FOR
 *                   subject (Equipment → Line → LineOperation → HumanLabor
 *                   → Process), walking to Site like equipment-detail
 *
 * Bounded by `LIMIT 10000`.
 */
export const PLANT_PROCEDURE_DETAIL_CYPHER = `
  MATCH (p:Procedure {id: $procedureId})

  CALL {
    WITH p
    OPTIONAL MATCH (p)-[:PROCEDURE_FOR]->(peer)
      WHERE peer:Equipment OR peer:Line OR peer:Process
         OR peer:LineOperation OR peer:HumanLabor
    RETURN [row IN collect(DISTINCT {
             peer: peer,
             peerLabels: labels(peer)
           }) WHERE row.peer IS NOT NULL] AS appliesTo
  }

  CALL {
    WITH p
    OPTIONAL MATCH (p)-[rp:REQUIRES_PART]->(part:Part)
    RETURN [row IN collect(DISTINCT {
             part: part,
             edge: {
               type: type(rp),
               source: p.id,
               target: part.id,
               props: properties(rp)
             }
           }) WHERE row.part IS NOT NULL] AS parts
  }

  CALL {
    WITH p
    OPTIONAL MATCH (p)-[rs:REQUIRES_SKILL_LEVEL]->(role:Role)
    RETURN [row IN collect(DISTINCT {
             role: role,
             props: properties(rs)
           }) WHERE row.role IS NOT NULL] AS skills
  }

  CALL {
    WITH p
    OPTIONAL MATCH (p)-[:APPLIES_TO_PRODUCT]->(product:Product)
    RETURN [n IN collect(DISTINCT product) WHERE n IS NOT NULL] AS products
  }

  CALL {
    WITH p
    OPTIONAL MATCH (p)-[:PROCEDURE_FOR]->(peer)
      WHERE peer:Equipment OR peer:Line OR peer:LineOperation
         OR peer:HumanLabor OR peer:Process
    WITH p, peer,
         CASE
           WHEN peer:Equipment THEN 0
           WHEN peer:Line THEN 1
           WHEN peer:LineOperation THEN 2
           WHEN peer:HumanLabor THEN 3
           WHEN peer:Process THEN 4
           ELSE 9
         END AS rank
    ORDER BY rank
    WITH p, head(collect(peer)) AS subject
    OPTIONAL MATCH (subject)<-[:HAS_EQUIPMENT]-(eqLine:Line)
    OPTIONAL MATCH (subject)<-[:HAS_LINE_OPERATION]-(opLine:Line)
    OPTIONAL MATCH (subject)<-[:REQUIRES_LABOR]-(laborLine:Line)
    OPTIONAL MATCH (subject)<-[:REQUIRES_LABOR]-(laborOp:LineOperation)
      <-[:HAS_LINE_OPERATION]-(laborOpLine:Line)
    OPTIONAL MATCH (subject)<-[:INSTANTIATES]-(processLine:Line)
    WITH p, subject,
         CASE
           WHEN subject:Line THEN subject
           ELSE coalesce(eqLine, opLine, laborLine, laborOpLine, processLine)
         END AS line
    OPTIONAL MATCH (line)<-[:HAS_SUBLINE*0..20]-(:Line)<-[:HAS_LINE]-(site:Site)
    RETURN subject AS breadcrumbSubject,
           CASE WHEN subject IS NULL THEN [] ELSE labels(subject) END AS breadcrumbSubjectLabels,
           line,
           head(collect(DISTINCT site)) AS site
  }

  RETURN p, appliesTo, parts, skills, products,
         site, line, breadcrumbSubject, breadcrumbSubjectLabels,
         ${isStrayCypherExpr('p', 'Procedure')} AS isStray
  LIMIT 10000
`
